/**
 * PURPOSE: Búsqueda, filtros y agrupación del catálogo de pruebas de Atletismo en Winscore.
 * RESPONSIBILITIES: Normalizar texto (minúsculas, sin tildes), aplicar la búsqueda tolerante por
 *   prefijos con sinónimos, filtrar por nivel/rama/tipo/estado/jornada, agrupar por disciplina y
 *   elegir el horario "siguiente" de una prueba. Puro: no toca red ni almacenamiento.
 *
 *   SOLO PRESENTACIÓN. Nada de esto decide puestos, puntos ni finales: ordena y filtra lo que el
 *   servidor ya devolvió (`PruebaSummary[]`) para que el operador encuentre la prueba rápido.
 * COLLABORATORS: area/AtletismoResultadosArea/PruebaListPanel.jsx.
 * ROUTE: winscore/src/utils/athleticsSearch.js
 */

export const DISCIPLINE_OPTIONS = Object.freeze([
  { value: "pista", label: "Pista" },
  { value: "vallas", label: "Vallas" },
  { value: "postas", label: "Postas" },
  { value: "saltos", label: "Saltos" },
  { value: "lanzamientos", label: "Lanzamientos" },
  { value: "combinadas", label: "Combinadas" },
  { value: "marcha", label: "Marcha" },
]);

export const LEVEL_OPTIONS = Object.freeze([
  { value: "AZ", label: "Avanzados" },
  { value: "NV", label: "Noveles" },
]);

export const BRANCH_OPTIONS = Object.freeze([
  { value: "F", label: "Damas" },
  { value: "M", label: "Varones" },
  { value: "X", label: "Mixta" },
]);

export const PRUEBA_STATUS_OPTIONS = Object.freeze([
  { value: "pending", label: "Pendiente" },
  { value: "in_progress", label: "En curso" },
  { value: "official", label: "Oficial" },
]);

export const EMPTY_PRUEBA_FILTERS = Object.freeze({
  query: "",
  level: "",
  branch: "",
  discipline: "",
  status: "",
  jornada: "",
});

/**
 * Sinónimos de captura rápida: si lo tecleado EMPIEZA por uno de los prefijos, también vale como la
 * palabra canónica ("muj" → "damas", "masculino" → "varones", "avz" → "avanzados").
 */
const SYNONYMS = Object.freeze([
  ["damas", ["dam", "muj", "fem"]],
  ["varones", ["var", "hom", "masc"]],
  ["noveles", ["nov"]],
  ["avanzados", ["av", "avz", "ava"]],
  ["mixta", ["mix"]],
]);

/** Minúsculas, sin tildes y sin signos: "100 Metros Avanzados Damas" → "100 metros avanzados damas". */
export const normalizeSearchText = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

export const tokenizeSearchQuery = (query) =>
  normalizeSearchText(query).split(" ").filter(Boolean);

const splitAlphaNumeric = (token) => token.match(/[a-z]+|[0-9]+/g) || [];

/** Palabras del texto más sus partes letra/número ("100m" aporta "100m", "100" y "m"). */
const buildWords = (text) => {
  const words = new Set();
  normalizeSearchText(text)
    .split(" ")
    .filter(Boolean)
    .forEach((word) => {
      words.add(word);
      splitAlphaNumeric(word).forEach((part) => words.add(part));
    });
  return Array.from(words);
};

export const expandSearchToken = (token) => {
  const alternatives = [token];
  SYNONYMS.forEach(([canonical, prefixes]) => {
    if (prefixes.some((prefix) => token.startsWith(prefix))) alternatives.push(canonical);
  });
  return alternatives;
};

const tokenMatches = (token, words) =>
  expandSearchToken(token).some((alternative) =>
    words.some((word) => word.startsWith(alternative))
  );

/**
 * Cada token tecleado tiene que ser prefijo de alguna palabra (o de su sinónimo). Un token mixto
 * como "100m" que no casa entero se prueba por partes ("100" y "m").
 */
export const matchesSearchQuery = (text, query) => {
  const tokens = tokenizeSearchQuery(query);
  if (!tokens.length) return true;
  const words = buildWords(text);
  return tokens.every((token) => {
    if (tokenMatches(token, words)) return true;
    const parts = splitAlphaNumeric(token);
    return parts.length > 1 && parts.every((part) => tokenMatches(part, words));
  });
};

export const pruebaSearchHaystack = (prueba) =>
  [
    prueba?.searchText,
    prueba?.label,
    prueba?.name,
    prueba?.code,
    prueba?.level?.name,
    prueba?.branch?.name,
    prueba?.discipline,
  ]
    .filter(Boolean)
    .join(" ");

const sameValue = (left, right) => String(left ?? "") === String(right ?? "");

export const pruebaInJornada = (prueba, jornada) => {
  if (jornada === "" || jornada === null || jornada === undefined) return true;
  if (sameValue(prueba?.jornada, jornada)) return true;
  return (Array.isArray(prueba?.schedule) ? prueba.schedule : []).some((item) =>
    sameValue(item?.jornada, jornada)
  );
};

export const filterPruebas = (pruebas, filters = EMPTY_PRUEBA_FILTERS) => {
  const list = Array.isArray(pruebas) ? pruebas : [];
  const current = { ...EMPTY_PRUEBA_FILTERS, ...(filters || {}) };
  return list.filter((prueba) => {
    if (current.level && !sameValue(prueba?.level?.code, current.level)) return false;
    if (current.branch && !sameValue(prueba?.branch?.code, current.branch)) return false;
    if (current.discipline && !sameValue(prueba?.discipline, current.discipline)) return false;
    if (current.status && !sameValue(prueba?.status, current.status)) return false;
    if (!pruebaInJornada(prueba, current.jornada)) return false;
    return matchesSearchQuery(pruebaSearchHaystack(prueba), current.query);
  });
};

export const hasActivePruebaFilters = (filters) =>
  Object.keys(EMPTY_PRUEBA_FILTERS).some((key) => String(filters?.[key] ?? "").trim() !== "");

/** Grupos en el orden fijo de disciplinas; una disciplina desconocida va al final, nunca se pierde. */
export const groupPruebasByDiscipline = (pruebas) => {
  const groups = new Map();
  (Array.isArray(pruebas) ? pruebas : []).forEach((prueba) => {
    const key = prueba?.discipline || "otras";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(prueba);
  });

  const ordered = [];
  DISCIPLINE_OPTIONS.forEach(({ value, label }) => {
    if (groups.has(value)) {
      ordered.push({ discipline: value, label, pruebas: groups.get(value) });
      groups.delete(value);
    }
  });
  groups.forEach((list, key) => {
    ordered.push({
      discipline: key,
      label: key === "otras" ? "Otras" : key.charAt(0).toUpperCase() + key.slice(1),
      pruebas: list,
    });
  });
  return ordered;
};

const scheduleTimestamp = (item) => {
  const date = String(item?.date || "");
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return null;
  const [hours, minutes] = String(item?.time || "00:00")
    .split(":")
    .map((part) => Number(part) || 0);
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), hours, minutes).getTime();
};

/**
 * Horario a mostrar en la lista: la primera fase que aún no empezó; si todas pasaron, la última.
 * Es solo una ayuda visual: el estado real de cada serie lo dice el detalle.
 */
export const pickNextSchedule = (schedule, now = new Date()) => {
  const list = (Array.isArray(schedule) ? schedule : []).filter(Boolean);
  if (!list.length) return null;
  const withTime = list
    .map((item, index) => ({ item, index, at: scheduleTimestamp(item) }))
    .sort((a, b) => {
      if (a.at === null && b.at === null) return a.index - b.index;
      if (a.at === null) return 1;
      if (b.at === null) return -1;
      return a.at - b.at;
    });
  const nowMs = now instanceof Date ? now.getTime() : Number(now);
  const upcoming = withTime.find((entry) => entry.at !== null && entry.at >= nowMs);
  return (upcoming || withTime[withTime.length - 1]).item;
};

/**
 * Orden por defecto de la lista: por día y hora de la próxima fase (lo atrasado o en curso queda arriba, luego lo que
 * viene); las oficializadas al final. Agrupa por jornada de esa fase; las oficializadas van en su propio grupo.
 */
export const groupPruebasBySchedule = (pruebas, jornadaLabels = new Map(), now = new Date()) => {
  const rows = (Array.isArray(pruebas) ? pruebas : []).map((prueba, index) => {
    const next = pickNextSchedule(prueba?.schedule, now);
    return { prueba, index, next, at: next ? scheduleTimestamp(next) : null, official: prueba?.status === "official" };
  });
  rows.sort((a, b) => {
    if (a.official !== b.official) return a.official ? 1 : -1;
    if (a.at === null && b.at === null) return a.index - b.index;
    if (a.at === null) return 1;
    if (b.at === null) return -1;
    return a.at - b.at || a.index - b.index;
  });
  const groups = [];
  rows.forEach((row) => {
    const key = row.official ? "official" : row.next?.jornada ? `j${row.next.jornada}` : "none";
    let group = groups[groups.length - 1];
    if (!group || group.key !== key) {
      const label =
        key === "official"
          ? "Oficializadas"
          : key === "none"
            ? "Sin horario"
            : jornadaLabels.get(String(row.next.jornada)) || `Jornada ${row.next.jornada}`;
      group = { key, label, pruebas: [] };
      groups.push(group);
    }
    group.pruebas.push(row.prueba);
  });
  return groups;
};
